import { useState, useEffect, useCallback, useRef } from "react";
import { ArrowRight, Download, Loader2, Share2, Quote, Copy, ChevronRight, Shield, Clock, BookOpen } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList, Cell,
} from "recharts";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import axios from "axios";
import { trackEvent } from "../../lib/track";
import { copyToClipboard } from "./compare/lib/clipboard";
import { ORIENTATION_COLORS, TYPES, FILTERS, LOADING_MESSAGES } from "./compare/lib/constants";
import { SearchSelect } from "./compare/components/SearchSelect";
import { MetricTile } from "./compare/components/MetricTile";
import { AicciGauge } from "./compare/components/AicciGauge";
import { ShareModal } from "./compare/components/ShareModal";
import { TimeMachine } from "./compare/sections/TimeMachine";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const CountryCompare = ({ entityType: initET = "" }) => {
  const [cType, setCType] = useState("countries");
  const [eType, setEType] = useState(initET);
  const [opts, setOpts] = useState(null);
  const [s1, setS1] = useState("");
  const [s2, setS2] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadMsg, setLoadMsg] = useState("");
  const [nlQuery, setNlQuery] = useState("");
  const [askAnswer, setAskAnswer] = useState("");
  const [error, setError] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [shareText, setShareText] = useState("");
  const [dling, setDling] = useState(false);
  const [userQuery, setUserQuery] = useState("");
  const [timeMachine, setTimeMachine] = useState(null);
  const [tmLoading, setTmLoading] = useState(false);
  const [tmOpen, setTmOpen] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/compare/options`).then(r => setOpts(r.data)).catch(() => {});
  }, []);

  const list = useCallback(() => opts?.[cType] || [], [opts, cType]);
  const switchType = (t) => { setCType(t); setS1(""); setS2(""); setData(null); setAskAnswer(""); };

  useEffect(() => {
    if (!loading) return;
    let i = 0;
    setLoadMsg(LOADING_MESSAGES[0]);
    const interval = setInterval(() => {
      i = (i + 1) % LOADING_MESSAGES.length;
      setLoadMsg(LOADING_MESSAGES[i]);
    }, 1500);
    return () => clearInterval(interval);
  }, [loading]);

  const runCompare = async () => {
    if (!s1 || !s2 || s1 === s2) return;
    const typeLabel = TYPES.find(t => t.id === cType)?.label || cType;
    const filterLabel = FILTERS.find(f => f.id === eType)?.label || "Universe";
    setUserQuery(`Compare ${s1} vs ${s2} in ${typeLabel} (${filterLabel})`);
    setLoading(true); setData(null); setAskAnswer(""); setError("");
    try {
      const r = await axios.get(`${API}/compare`, {
        params: { compare_type: cType, item1: s1, item2: s2, entity_type: eType },
      });
      setData(r.data);
    } catch {
      setError("Comparison failed. Please try again.");
    }
    setLoading(false);
  };

  const runAsk = async () => {
    if (!nlQuery.trim()) return;
    setUserQuery(nlQuery.trim());
    setLoading(true); setData(null); setAskAnswer(""); setError("");
    trackEvent("compare_query", "compare", nlQuery.trim().slice(0, 100));
    try {
      const r = await axios.get(`${API}/compare/ask`, {
        params: { query: nlQuery.trim(), entity_type: eType },
      });
      setAskAnswer(r.data?.answer || "");
      if (r.data?.comparison) {
        setData(r.data.comparison);
        const comp = r.data.comparison;
        if (comp.item1?.label && comp.item2?.label) {
          axios.get(`${API}/compare/historical`, {
            params: {
              compare_type: comp.compare_type,
              item1: comp.item1.label,
              item2: comp.item2.label,
              entity_type: comp.entity_type === "all" ? "" : comp.entity_type,
            },
          }).then(hr => setTimeMachine(hr.data)).catch(() => {});
        }
      }
    } catch {
      setError("Could not process your question. Try a simpler query.");
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => { if (e.key === "Enter") runAsk(); };

  const a = data?.item1;
  const b = data?.item2;
  const hasResults = a?.total > 0 && b?.total > 0;

  const downloadPNG = async () => {
    if (!cardRef.current) return;
    setDling(true);
    trackEvent("compare_download", "compare");
    const fileName = `AIOO_${a?.label || "A"}_vs_${b?.label || "B"}.png`;
    const saveFromDataUrl = (dataUrl) => {
      const link = document.createElement("a");
      link.download = fileName;
      link.href = dataUrl;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      setTimeout(() => document.body.removeChild(link), 200);
    };

    try {
      let dataUrl;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          dataUrl = await toPng(cardRef.current, {
            backgroundColor: "#060a12",
            pixelRatio: 2,
            skipFonts: true,
            cacheBust: true,
            style: { padding: "32px" },
          });
          if (dataUrl && dataUrl.length > 100) break;
        } catch { /* retry */ }
      }
      if (!dataUrl || dataUrl.length < 100) throw new Error("Empty");
      saveFromDataUrl(dataUrl);
      toast.success("PNG downloaded");
    } catch {
      try {
        const { toBlob } = await import("html-to-image");
        const blob = await toBlob(cardRef.current, {
          backgroundColor: "#060a12",
          pixelRatio: 2,
          skipFonts: true,
          cacheBust: true,
        });
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.download = fileName;
          link.href = url;
          link.style.display = "none";
          document.body.appendChild(link);
          link.click();
          setTimeout(() => { document.body.removeChild(link); URL.revokeObjectURL(url); }, 200);
          toast.success("PNG downloaded");
        }
      } catch {
        toast.error("Download failed. Try right-clicking and 'Save as image'.");
      }
    }
    setDling(false);
  };

  const shareInsight = (text) => {
    setShareText(text);
    setShareOpen(true);
    trackEvent("compare_share", "compare");
  };

  const loadTimeMachine = async () => {
    if (!data || !a || !b) return;
    setTmLoading(true);
    try {
      const r = await axios.get(`${API}/compare/historical`, {
        params: {
          compare_type: data.compare_type,
          item1: a.label,
          item2: b.label,
          entity_type: data.entity_type === "all" ? "" : data.entity_type,
        },
      });
      setTimeMachine(r.data);
      setTmOpen(true);
    } catch {
      toast.error("Failed to load historical data");
    }
    setTmLoading(false);
  };

  const citeData = async () => {
    const cite = `AI Orientation Observatory. (${new Date().getFullYear()}). Comparison: ${a?.label} vs ${b?.label}. Retrieved from ${window.location.origin}`;
    const ok = await copyToClipboard(cite);
    toast(ok ? "Citation copied to clipboard" : "Could not copy — please copy manually", { duration: 2500 });
  };

  const copyText = async (text) => {
    const ok = await copyToClipboard(text);
    toast(ok ? "Copied" : "Copy failed", { duration: 1500 });
  };

  const chartData = hasResults
    ? ["Care", "Utility", "Creative", "Extraction"].map(o => ({
        name: o,
        [a.label]: a.orientation?.[o.toLowerCase()] || 0,
        [b.label]: b.orientation?.[o.toLowerCase()] || 0,
      }))
    : [];
  const appUrl = typeof window !== "undefined" ? window.location.href : "";

  const headline = (() => {
    if (!hasResults) return "";
    if (askAnswer) {
      const firstSentence = askAnswer.split(/[.!]/).filter(s => s.trim())[0]?.trim();
      if (firstSentence && firstSentence.length < 120) return firstSentence;
    }
    const ins = data?.insights || [];
    if (ins.length === 0) return `${a.label} vs ${b.label}`;
    const scaleInsight = ins.find(i => /\dx more/.test(i));
    const careInsight = ins.find(i => /care-oriented/.test(i));
    if (scaleInsight && careInsight) {
      const bigger = a.total > b.total ? a.label : b.label;
      const caring = a.care_pct > b.care_pct ? a.label : b.label;
      if (bigger !== caring) return `${bigger} builds more, ${caring} builds with more care`;
      return `${bigger} leads in both scale and care-oriented AI`;
    }
    if (careInsight) return careInsight;
    if (scaleInsight) return scaleInsight;
    return ins[0];
  })();

  let entityLabel = "Universe (All)";
  if (eType === "device") entityLabel = "Physical AI";
  else if (eType === "application") entityLabel = "Software Apps";

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-12" data-testid="universal-compare">
      {/* Hero */}
      <div className="text-center pt-4 pb-2">
        <h2 className="text-2xl font-bold text-slate-100 tracking-tight">What do you want to compare?</h2>
        <p className="text-sm text-slate-400 mt-1">Type naturally or select from the options below</p>
      </div>

      {/* Natural Language Bar */}
      <div className="relative">
        <input
          value={nlQuery}
          onChange={e => setNlQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder='Try "India vs China in care AI" or "Wearables vs Robotics"...'
          data-testid="compare-nl-input"
          className="w-full px-5 py-4 bg-black/40 border border-white/[0.08] rounded-2xl text-base text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/40 focus:border-violet-500/30 focus:shadow-[0_0_30px_rgba(139,92,246,0.15)] transition-all"
        />
        <button onClick={runAsk} disabled={!nlQuery.trim() || loading} data-testid="compare-nl-submit"
          className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 bg-violet-500/20 hover:bg-violet-500/30 disabled:opacity-30 text-violet-300 text-xs font-semibold rounded-xl border border-violet-500/20 transition-all">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Ask"}
        </button>
      </div>

      {/* Quick examples */}
      {!data && !loading && (
        <div className="flex gap-2 flex-wrap justify-center">
          {["India vs USA", "Care vs Extraction", "Europe vs Asia Pacific", "Wearables vs Robotics"].map(ex => (
            <button key={ex} onClick={() => setNlQuery(ex)}
              className="text-[11px] px-3 py-1.5 rounded-full bg-white/[0.03] border border-white/[0.06] text-slate-400 hover:text-violet-300 hover:border-violet-500/20 transition-all">
              {ex}
            </button>
          ))}
        </div>
      )}

      {/* Divider */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-white/[0.05]" />
        <span className="text-[10px] text-slate-500 uppercase tracking-widest font-medium">or select manually</span>
        <div className="flex-1 h-px bg-white/[0.05]" />
      </div>

      {/* Guided Controls */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          {TYPES.map(t => (
            <button key={t.id} onClick={() => switchType(t.id)}
              className={`px-3 py-1.5 rounded-xl text-[11px] font-medium transition-all ${
                cType === t.id
                  ? "bg-violet-500/15 text-violet-300 border border-violet-500/20"
                  : "text-slate-400 border border-transparent hover:text-slate-300"
              }`}>
              {t.label}
            </button>
          ))}
          <div className="h-4 w-px bg-white/[0.06] mx-1" />
          {FILTERS.map(f => (
            <button key={f.id} onClick={() => setEType(f.id)}
              className={`px-3 py-1.5 rounded-xl text-[11px] font-medium transition-all ${
                eType === f.id
                  ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/20"
                  : "text-slate-400 border border-transparent hover:text-slate-300"
              }`}>
              {f.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <SearchSelect value={s1} onChange={v => { setS1(v); setData(null); }} options={list()} placeholder="First item..." testId="compare-item1" />
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white/[0.03] border border-white/[0.06] flex items-center justify-center">
            <ArrowRight className="w-3.5 h-3.5 text-slate-500" />
          </div>
          <SearchSelect value={s2} onChange={v => { setS2(v); setData(null); }} options={list()} placeholder="Second item..." testId="compare-item2" excludeValue={s1} />
          <button onClick={runCompare} disabled={loading || !s1 || !s2 || s1 === s2} data-testid="compare-submit"
            className="px-5 py-2.5 bg-emerald-500/90 hover:bg-emerald-500 disabled:opacity-30 text-white text-xs font-bold rounded-xl flex-shrink-0 transition-all shadow-lg shadow-emerald-500/20">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Compare"}
          </button>
        </div>
      </div>

      {/* CONVERSATION THREAD */}
      {(userQuery || loading) && (
        <div className="space-y-4 mt-2">
          {userQuery && (
            <div className="flex justify-end animate-fade-in">
              <div className="max-w-[80%] bg-violet-500/10 border border-violet-500/15 rounded-2xl rounded-tr-sm px-5 py-3" data-testid="user-query-bubble">
                <p className="text-sm text-violet-200 leading-relaxed">{userQuery}</p>
              </div>
            </div>
          )}

          {loading && (
            <div className="flex justify-start animate-fade-in" data-testid="compare-loading">
              <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl rounded-tl-sm px-5 py-4 flex items-center gap-3">
                <span className="relative">
                  <span className="absolute w-5 h-5 rounded-full bg-violet-500/20 animate-ping" />
                  <span className="relative w-5 h-5 rounded-full bg-gradient-to-r from-violet-400 to-emerald-400 block shadow-[0_0_12px_rgba(139,92,246,0.5)]" />
                </span>
                <span className="text-xs text-slate-400 animate-pulse font-mono">{loadMsg}</span>
              </div>
            </div>
          )}

          {error && (
            <div className="flex justify-start animate-fade-in">
              <div className="max-w-[80%] text-xs text-red-400 bg-red-500/[0.06] border border-red-500/10 rounded-2xl rounded-tl-sm px-5 py-3" data-testid="compare-error">
                {error}
              </div>
            </div>
          )}

          {(askAnswer || hasResults) && !loading && (
            <div className="flex justify-start animate-fade-in">
              <div className="max-w-[90%] space-y-4">
                <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl rounded-tl-sm px-5 py-4" data-testid="ai-response-bubble">
                  <div className="flex items-start gap-3">
                    <span className="relative flex-shrink-0 mt-1">
                      <span className="absolute w-4 h-4 rounded-full bg-emerald-500/20 animate-ping" style={{ animationDuration: "2s" }} />
                      <span className="relative w-4 h-4 rounded-full bg-gradient-to-r from-emerald-400 to-violet-400 block shadow-[0_0_10px_rgba(16,185,129,0.5)]" />
                    </span>
                    <div className="flex-1">
                      {askAnswer && <p className="text-sm text-slate-200 leading-relaxed mb-3">{askAnswer}</p>}
                      {hasResults && (
                        <>
                          <h3 className="text-lg font-bold text-slate-100 leading-snug" data-testid="compare-headline">
                            {headline}
                          </h3>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <span className="text-xs font-semibold text-emerald-400">{a.label}</span>
                            <span className="text-[10px] text-slate-500">({a.total?.toLocaleString()})</span>
                            <span className="text-[10px] text-slate-700">vs</span>
                            <span className="text-xs font-semibold text-blue-400">{b.label}</span>
                            <span className="text-[10px] text-slate-500">({b.total?.toLocaleString()})</span>
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/[0.06] text-slate-400">
                              {entityLabel}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  {(askAnswer || hasResults) && (
                    <div className="flex gap-2 mt-3 ml-7">
                      <button onClick={() => shareInsight(askAnswer || headline)} className="text-[10px] text-violet-400 hover:text-violet-300 flex items-center gap-1 transition-all" data-testid="inline-share">
                        <Share2 className="w-3 h-3" />Share
                      </button>
                      <button onClick={() => copyText(askAnswer || headline)} className="text-[10px] text-slate-400 hover:text-slate-300 flex items-center gap-1 transition-all" data-testid="inline-copy">
                        <Copy className="w-3 h-3" />Copy
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {data && !hasResults && !loading && (
            <div className="flex justify-start animate-fade-in">
              <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl rounded-tl-sm px-5 py-3 text-sm text-slate-400" data-testid="compare-no-results">
                No data found for this comparison. Try different items or a broader filter.
              </div>
            </div>
          )}
        </div>
      )}

      {/* ANALYSIS */}
      {hasResults && !loading && (
        <div ref={cardRef} className="animate-fade-in space-y-5 mt-2" data-testid="compare-results">
          {/* AICCI */}
          {(a.aicci != null || b.aicci != null) && (
            <div className="bg-gradient-to-r from-violet-500/[0.06] to-emerald-500/[0.06] border border-white/[0.08] rounded-2xl p-5" data-testid="aicci-comparison">
              <div className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold mb-4 flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5 text-violet-400" />
                Consciousness Score (AICCI)
              </div>
              <div className="flex items-center justify-center gap-12">
                <AicciGauge score={a.aicci || 0} label={a.label} />
                <div className="flex flex-col items-center gap-1">
                  <span className="text-[10px] text-slate-500 font-bold">VS</span>
                  {a.aicci !== b.aicci && (
                    <div className="flex items-center gap-1 mt-1">
                      <ChevronRight className={`w-3 h-3 ${(a.aicci || 0) > (b.aicci || 0) ? "text-emerald-400 rotate-180" : "text-emerald-400"}`} />
                      <span className="text-[10px] text-emerald-400 font-semibold">
                        {Math.abs((a.aicci || 0) - (b.aicci || 0)).toFixed(1)} pts
                      </span>
                    </div>
                  )}
                </div>
                <AicciGauge score={b.aicci || 0} label={b.label} />
              </div>
              <p className="text-[10px] text-slate-500 text-center mt-3">
                Measures care orientation, founder diversity, inclusion, and ethical restraint
              </p>
            </div>
          )}

          {/* Insights */}
          {data.insights?.length > 0 && (
            <div className="space-y-2">
              {data.insights.map((insight, i) => (
                <div key={insight} className="group bg-white/[0.02] border border-white/[0.05] rounded-xl px-4 py-3 flex items-start gap-3 hover:border-white/[0.1] transition-all">
                  <span className="relative flex-shrink-0 mt-1.5">
                    <span className="absolute w-3 h-3 rounded-full bg-violet-500/30 animate-ping" style={{ animationDuration: `${2 + i * 0.5}s` }} />
                    <span className="relative w-3 h-3 rounded-full bg-gradient-to-r from-violet-400 to-emerald-400 block shadow-[0_0_8px_rgba(139,92,246,0.6)]" />
                  </span>
                  <p className="text-sm text-slate-300 flex-1 leading-relaxed">{insight}</p>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    <button onClick={() => shareInsight(insight)} title="Share" data-testid={`share-insight-${i}`}
                      className="p-1.5 rounded-lg hover:bg-white/[0.05] text-slate-400 hover:text-violet-400 transition-all">
                      <Share2 className="w-3 h-3" />
                    </button>
                    <button onClick={() => copyText(insight)} title="Copy" data-testid={`copy-insight-${i}`}
                      className="p-1.5 rounded-lg hover:bg-white/[0.05] text-slate-400 hover:text-slate-300 transition-all">
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricTile label="Total" v1={a.total?.toLocaleString()} v2={b.total?.toLocaleString()} name1={a.label} name2={b.label} />
            <MetricTile label="Care" v1={a.care_pct} v2={b.care_pct} suffix="%" name1={a.label} name2={b.label} />
            <MetricTile label="Creative" v1={a.creative_pct} v2={b.creative_pct} suffix="%" name1={a.label} name2={b.label} />
            <MetricTile label="Utility" v1={a.utility_pct} v2={b.utility_pct} suffix="%" name1={a.label} name2={b.label} higherIsBetter={false} />
            <MetricTile label="Extraction" v1={a.extraction_pct} v2={b.extraction_pct} suffix="%" name1={a.label} name2={b.label} higherIsBetter={false} />
            <MetricTile label="Female Founders" v1={a.female_pct} v2={b.female_pct} suffix="%" name1={a.label} name2={b.label} />
            <MetricTile label="Diversity" v1={a.diversity_pct} v2={b.diversity_pct} suffix="%" name1={a.label} name2={b.label} />
            <MetricTile label="Accessibility" v1={a.accessibility} v2={b.accessibility} name1={a.label} name2={b.label} />
          </div>

          {/* Research Comparison */}
          {(data.research1?.total > 0 || data.research2?.total > 0) && (
            <div className="bg-gradient-to-b from-cyan-500/[0.04] to-transparent border border-cyan-500/10 rounded-2xl p-5" data-testid="research-comparison">
              <div className="text-[10px] text-cyan-400 uppercase tracking-widest font-semibold mb-4 flex items-center gap-1.5">
                <BookOpen className="w-3.5 h-3.5" />
                Research Landscape Comparison
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MetricTile label="Research Papers" v1={data.research1?.total?.toLocaleString() || "0"} v2={data.research2?.total?.toLocaleString() || "0"} name1={a.label} name2={b.label} />
                <MetricTile label="Research Care" v1={data.research1?.care_pct || 0} v2={data.research2?.care_pct || 0} suffix="%" name1={a.label} name2={b.label} />
                <MetricTile label="Avg Citations" v1={data.research1?.avg_citations || 0} v2={data.research2?.avg_citations || 0} name1={a.label} name2={b.label} />
                <MetricTile label="Ethical Focus" v1={data.research1?.ethical_pct || 0} v2={data.research2?.ethical_pct || 0} suffix="%" name1={a.label} name2={b.label} />
              </div>
            </div>
          )}

          {/* Orientation Chart */}
          <div className="bg-gradient-to-b from-white/[0.04] to-white/[0.01] border border-white/[0.08] rounded-2xl p-6">
            <div className="text-xs text-slate-300 uppercase tracking-widest font-semibold mb-5">Orientation Distribution</div>
            <div style={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} barGap={6} barSize={36}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 12, fontWeight: 600 }} stroke="rgba(255,255,255,0.08)" />
                  <YAxis tick={{ fill: "#64748b", fontSize: 10 }} stroke="rgba(255,255,255,0.08)" />
                  <Tooltip
                    contentStyle={{ background: "#0f1a2e", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, fontSize: 12, color: "#e2e8f0", boxShadow: "0 8px 32px rgba(0,0,0,0.4)" }}
                    cursor={{ fill: "rgba(255,255,255,0.04)" }}
                  />
                  <Bar dataKey={a.label} radius={[6, 6, 0, 0]}>
                    {chartData.map((entry) => (
                      <Cell key={`a-${entry.name}`} fill={ORIENTATION_COLORS[entry.name] || "#34d399"} fillOpacity={1} />
                    ))}
                    <LabelList dataKey={a.label} position="top" fill="#e2e8f0" fontSize={11} fontWeight={600} />
                  </Bar>
                  <Bar dataKey={b.label} radius={[6, 6, 0, 0]}>
                    {chartData.map((entry) => (
                      <Cell key={`b-${entry.name}`} fill={ORIENTATION_COLORS[entry.name] || "#60a5fa"} fillOpacity={0.55} />
                    ))}
                    <LabelList dataKey={b.label} position="top" fill="#94a3b8" fontSize={10} fontWeight={500} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center gap-8 mt-3 text-xs">
              <span className="flex items-center gap-2"><span className="w-4 h-2.5 rounded-sm" style={{ background: "#10b981" }} /><span className="text-slate-300 font-medium">{a.label}</span></span>
              <span className="flex items-center gap-2"><span className="w-4 h-2.5 rounded-sm" style={{ background: "#10b981", opacity: 0.55 }} /><span className="text-slate-300 font-medium">{b.label}</span></span>
            </div>
          </div>

          {/* Action Bar */}
          <div className="flex items-center justify-center gap-3 pt-2">
            <button
              onClick={() => { if (!timeMachine) loadTimeMachine(); else setTmOpen(!tmOpen); }}
              disabled={tmLoading}
              data-testid="time-machine-toggle"
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500/15 to-violet-500/15 hover:from-amber-500/25 hover:to-violet-500/25 border border-amber-500/20 rounded-xl text-xs text-amber-300 font-medium transition-all"
            >
              <Clock className="w-3.5 h-3.5" />
              {tmLoading ? "Loading..." : tmOpen ? "Hide Time Machine" : "Time Machine"}
            </button>
            <button onClick={() => shareInsight(data.insights?.join(". ") || `${a.label} vs ${b.label} comparison`)} data-testid="share-button"
              className="flex items-center gap-2 px-5 py-2.5 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] rounded-xl text-xs text-slate-300 font-medium transition-all">
              <Share2 className="w-3.5 h-3.5" />Share
            </button>
            <button onClick={citeData} data-testid="cite-button"
              className="flex items-center gap-2 px-5 py-2.5 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] rounded-xl text-xs text-slate-300 font-medium transition-all">
              <Quote className="w-3.5 h-3.5" />Cite
            </button>
            <button onClick={downloadPNG} disabled={dling} data-testid="compare-download"
              className="flex items-center gap-2 px-5 py-2.5 bg-violet-500/15 hover:bg-violet-500/25 border border-violet-500/20 rounded-xl text-xs text-violet-300 font-medium transition-all">
              <Download className="w-3.5 h-3.5" />{dling ? "Generating..." : "Download PNG"}
            </button>
          </div>

          {/* Time Machine */}
          {tmOpen && <TimeMachine timeMachine={timeMachine} />}

          {/* Attribution */}
          <div className="flex items-center justify-between px-2 pt-3 border-t border-white/[0.04]">
            <span className="text-[10px] text-slate-500">&copy; {new Date().getFullYear()} AI Orientation Observatory</span>
            <span className="text-[10px] text-slate-500">amplifyingconsciousinnovation.com</span>
          </div>
        </div>
      )}

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} text={shareText} url={appUrl} />
    </div>
  );
};

export default CountryCompare;
